/**
 * Canonical status vocabulary.
 *
 * The approved artwork used three different vocabularies for the same
 * lifecycle: the phone tracker showed seven steps ("Donation Created", "Food
 * Preparing", "Ready for Distribution"), the desktop tracker showed six with
 * different labels ("Order Placed", "Preparing", no ready state), and the
 * status pills used a fourth set. See gaps G-06 and G-24 in
 * Requirements/SCREEN_SPECIFICATIONS.md.
 *
 * This module is the single resolution. The server owns the enum; both clients
 * import these maps and never invent a label or pick a colour themselves.
 */

import type { StatusTone } from '@mfa/theme';

/** Order lifecycle. Mirrors the server's `OrderStatus` enum exactly. */
export const ORDER_STATUS = [
  'NEW',
  'PAYMENT_CONFIRMED',
  'ACCEPTED',
  'PREPARING',
  'READY_FOR_DISTRIBUTION',
  'DISTRIBUTED',
  'COMPLETED',
  'PAYMENT_FAILED',
  'CANCELLED',
  'REFUND_PENDING',
  'REFUNDED',
] as const;

export type OrderStatus = (typeof ORDER_STATUS)[number];

/** Payment lifecycle, tracked separately from the order. */
export const PAYMENT_STATUS = [
  'PENDING',
  'PROCESSING',
  'PAID',
  'FAILED',
  'REFUND_PENDING',
  'REFUNDED',
] as const;

export type PaymentStatus = (typeof PAYMENT_STATUS)[number];

/** Distribution lifecycle, tracked separately from the order. */
export const DISTRIBUTION_STATUS = [
  'NOT_SCHEDULED',
  'SCHEDULED',
  'IN_PROGRESS',
  'DISTRIBUTED',
  'VERIFIED',
] as const;

export type DistributionStatus = (typeof DISTRIBUTION_STATUS)[number];

interface StatusMeta {
  /** Donor-facing label. Adopted from the phone artwork, which was fullest. */
  label: string;
  /** Admin-facing label; terser for table columns. */
  adminLabel: string;
  /** Theme tone. One tone per status across donor and admin — resolves G-06. */
  tone: StatusTone;
  /** True when the order has stopped progressing. */
  terminal: boolean;
}

export const orderStatusMeta: Record<OrderStatus, StatusMeta> = {
  NEW: { label: 'Donation Created', adminLabel: 'New', tone: 'neutral', terminal: false },
  PAYMENT_CONFIRMED: {
    label: 'Payment Confirmed',
    adminLabel: 'Paid',
    tone: 'info',
    terminal: false,
  },
  ACCEPTED: { label: 'Order Accepted', adminLabel: 'Accepted', tone: 'info', terminal: false },
  PREPARING: { label: 'Food Preparing', adminLabel: 'Preparing', tone: 'progress', terminal: false },
  READY_FOR_DISTRIBUTION: {
    label: 'Ready for Distribution',
    adminLabel: 'Ready',
    tone: 'progress',
    terminal: false,
  },
  DISTRIBUTED: {
    label: 'Distributed',
    adminLabel: 'Distributed',
    tone: 'success',
    terminal: false,
  },
  COMPLETED: { label: 'Completed', adminLabel: 'Completed', tone: 'success', terminal: true },
  PAYMENT_FAILED: {
    label: 'Payment Failed',
    adminLabel: 'Payment Failed',
    tone: 'danger',
    terminal: true,
  },
  CANCELLED: { label: 'Cancelled', adminLabel: 'Cancelled', tone: 'danger', terminal: true },
  REFUND_PENDING: {
    label: 'Refund Pending',
    adminLabel: 'Refund Pending',
    tone: 'progress',
    terminal: false,
  },
  REFUNDED: { label: 'Refunded', adminLabel: 'Refunded', tone: 'neutral', terminal: true },
};

export const paymentStatusMeta: Record<PaymentStatus, StatusMeta> = {
  PENDING: { label: 'Pending', adminLabel: 'Pending', tone: 'neutral', terminal: false },
  PROCESSING: { label: 'Processing', adminLabel: 'Processing', tone: 'progress', terminal: false },
  PAID: { label: 'Paid', adminLabel: 'Paid', tone: 'success', terminal: true },
  FAILED: { label: 'Failed', adminLabel: 'Failed', tone: 'danger', terminal: true },
  REFUND_PENDING: {
    label: 'Refund Pending',
    adminLabel: 'Refund Pending',
    tone: 'progress',
    terminal: false,
  },
  REFUNDED: { label: 'Refunded', adminLabel: 'Refunded', tone: 'neutral', terminal: true },
};

export const distributionStatusMeta: Record<DistributionStatus, StatusMeta> = {
  NOT_SCHEDULED: {
    label: 'Not Scheduled',
    adminLabel: 'Unscheduled',
    tone: 'neutral',
    terminal: false,
  },
  SCHEDULED: { label: 'Scheduled', adminLabel: 'Scheduled', tone: 'info', terminal: false },
  IN_PROGRESS: { label: 'In Progress', adminLabel: 'In Progress', tone: 'progress', terminal: false },
  DISTRIBUTED: { label: 'Distributed', adminLabel: 'Distributed', tone: 'success', terminal: false },
  VERIFIED: { label: 'Verified', adminLabel: 'Verified', tone: 'success', terminal: true },
};

/**
 * The seven steps a donor sees on the tracking timeline, in order.
 *
 * Both the phone (vertical) and desktop (horizontal) trackers render exactly
 * this list. The desktop design omitted READY_FOR_DISTRIBUTION; that omission
 * is not reproduced.
 */
export const TRACKING_STEPS: readonly OrderStatus[] = [
  'NEW',
  'PAYMENT_CONFIRMED',
  'ACCEPTED',
  'PREPARING',
  'READY_FOR_DISTRIBUTION',
  'DISTRIBUTED',
  'COMPLETED',
] as const;

/** Statuses that mean "this donation is still on its way". Drives the Active filter. */
export const ACTIVE_STATUSES: readonly OrderStatus[] = [
  'NEW',
  'PAYMENT_CONFIRMED',
  'ACCEPTED',
  'PREPARING',
  'READY_FOR_DISTRIBUTION',
  'DISTRIBUTED',
] as const;

/** The filter chips on My Donations. Adds Pending and Cancelled, per gap G-16. */
export const DONATION_FILTERS = ['all', 'active', 'pending', 'completed', 'cancelled'] as const;
export type DonationFilter = (typeof DONATION_FILTERS)[number];

export const donationFilterLabel: Record<DonationFilter, string> = {
  all: 'All',
  active: 'Active',
  pending: 'Pending',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

export const DONATION_SORTS = ['newest', 'oldest', 'upcoming'] as const;
export type DonationSort = (typeof DONATION_SORTS)[number];

export const donationSortLabel: Record<DonationSort, string> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  upcoming: 'Upcoming distribution',
};

export function matchesFilter(status: OrderStatus, filter: DonationFilter): boolean {
  switch (filter) {
    case 'all':
      return true;
    case 'active':
      return ACTIVE_STATUSES.includes(status);
    case 'pending':
      return status === 'NEW';
    case 'completed':
      return status === 'COMPLETED';
    case 'cancelled':
      return status === 'CANCELLED' || status === 'PAYMENT_FAILED' || status === 'REFUNDED';
  }
}

/** Index of a status within the tracking timeline, or -1 for exceptional states. */
export const trackingIndex = (status: OrderStatus): number => TRACKING_STEPS.indexOf(status);

export const isTerminal = (status: OrderStatus): boolean => orderStatusMeta[status].terminal;

/**
 * Whether a donation has reached the point where proof of distribution can
 * exist. Used to decide whether to offer the proof screen at all.
 */
export const hasReachedDistribution = (status: OrderStatus): boolean => {
  const i = trackingIndex(status);
  return i >= trackingIndex('DISTRIBUTED');
};
